import React from "react";
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Button,
	Box,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

interface AlertDialogProps {
	open: boolean;
	title: string;
	message: React.ReactNode;
	severity?: "warning" | "info" | "error" | "success";
	buttonText?: string;
	onClose: () => void;
	maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
}

const AlertDialog: React.FC<AlertDialogProps> = ({
	open,
	title,
	message,
	severity = "info",
	buttonText = "OK",
	onClose,
	maxWidth = "xs",
}) => {
	const getIcon = () => {
		switch (severity) {
			case "warning":
				return (
					<WarningAmberIcon sx={{ color: "warning.main", fontSize: 36 }} />
				);
			case "error":
				return <ErrorOutlineIcon sx={{ color: "error.main", fontSize: 36 }} />;
			case "success":
				return (
					<CheckCircleOutlineIcon
						sx={{ color: "success.main", fontSize: 36 }}
					/>
				);
			case "info":
			default:
				return <InfoOutlinedIcon sx={{ color: "info.main", fontSize: 36 }} />;
		}
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth={maxWidth}
			aria-labelledby="alert-dialog-title"
			aria-describedby="alert-dialog-description"
		>
			<DialogTitle id="alert-dialog-title" sx={{ pb: 1 }}>
				<Box display="flex" alignItems="center" gap={1}>
					{getIcon()}
					{title}
				</Box>
			</DialogTitle>
			<DialogContent>
				<DialogContentText id="alert-dialog-description">
					{message}
				</DialogContentText>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button
					onClick={onClose}
					variant="contained"
					color={severity === "error" ? "error" : "primary"}
					autoFocus
				>
					{buttonText}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default AlertDialog;
